<script setup>
import { computed, onMounted } from 'vue';
import { useToast } from '@vuejs/composables/useToast';

const toastStore = useToast();
const toasts = computed(() => toastStore.toasts.value);

const variantIcon = (variant) => {
    if (variant === 'error') return ['fal', 'circle-xmark'];
    if (variant === 'warning') return ['fal', 'triangle-exclamation'];
    if (variant === 'info') return ['fal', 'circle-info'];
    return ['fal', 'circle-check'];
};

onMounted(() => {
    toastStore.consumePending();
});
</script>

<template>
    <div class="toast-app">
        <TransitionGroup name="toast-app" tag="div" class="toast-app__list">
            <div v-for="toast in toasts" :key="toast.id" class="toast-app__item"
                :class="`toast-app__item--${toast.variant || 'success'}`" role="status" aria-live="polite" aria-atomic="true">
                <font-awesome-icon :icon="variantIcon(toast.variant)" class="toast-app__icon" />
                <span class="toast-app__message">{{ toast.message }}</span>
                <button type="button" class="toast-app__close" aria-label="Zamknij" @click="toastStore.hide(toast.id)">
                    <font-awesome-icon :icon="['fal', 'xmark']" />
                </button>
            </div>
        </TransitionGroup>
    </div>
</template>

<style lang="scss" scoped>
.toast-app {
    position: fixed;
    top: 1.5rem;
    right: 1.5rem;
    z-index: 1090;
    pointer-events: none;
    max-width: 420px;
    width: calc(100vw - 2rem);
}

.toast-app__list {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
}

.toast-app__item {
    pointer-events: auto;
    display: flex;
    align-items: center;
    gap: 0.875rem;
    padding: 1rem 1.25rem;
    background-color: var(--bs-primary);
    color: #fff;
    border-radius: 4px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.18);
    font-size: 0.9375rem;
    line-height: 1.4;
}

.toast-app__icon {
    font-size: 1.375rem;
    flex-shrink: 0;
    color: #fff;
}

.toast-app__item--error .toast-app__icon {
    color: #ff8a8a;
}

.toast-app__item--warning .toast-app__icon {
    color: #ffd28a;
}

.toast-app__item--info .toast-app__icon {
    color: #9bd1ff;
}

.toast-app__message {
    flex-grow: 1;
    font-weight: 400;
}

.toast-app__close {
    flex-shrink: 0;
    background: transparent;
    border: 0;
    padding: 0.25rem 0.375rem;
    margin: -0.25rem -0.5rem -0.25rem 0;
    color: #fff;
    opacity: 0.65;
    font-size: 1rem;
    line-height: 1;
    cursor: pointer;
    transition: opacity 0.15s ease;
}

.toast-app__close:hover,
.toast-app__close:focus-visible {
    opacity: 1;
    outline: none;
}

.toast-app-enter-active,
.toast-app-leave-active {
    transition: opacity 0.3s ease, transform 0.3s cubic-bezier(0.22, 1, 0.36, 1);
}

.toast-app-enter-from {
    opacity: 0;
    transform: translateX(120%);
}

.toast-app-leave-to {
    opacity: 0;
    transform: translateX(120%);
}

.toast-app-leave-active {
    position: absolute;
    right: 0;
    left: 0;
}

@media (max-width: 575.98px) {
    .toast-app {
        top: 1rem;
        right: 1rem;
        left: 1rem;
        max-width: none;
        width: auto;
    }

    .toast-app__item {
        padding: 0.875rem 1rem;
        font-size: 0.875rem;
    }
}
</style>
